/**
 * Group child type validation utilities.
 *
 * Enforces same-category constraint: all children in a group must be
 * the same media category (all image or all video). Unsupported node
 * types are rejected.
 */

export type MediaCategory = 'image' | 'video'

/**
 * Maps supported node types to their media category.
 * Any type not in this map is unsupported and will be rejected.
 */
const MEDIA_CATEGORIES: Record<string, MediaCategory> = {
  imageGenerator: 'image',
  generatedImageWithHistory: 'image',
  image: 'image',
  videoGenerator: 'video',
  video: 'video',
}

/**
 * Returns the media category for a node type, or null if unsupported.
 * Unsupported types include: characterDesigner, locationDesigner,
 * objectDesigner, scriptWriter, soundExpert, videoEditor, audio,
 * and any unknown type.
 */
export function getMediaCategory(nodeType: string): MediaCategory | null {
  return MEDIA_CATEGORIES[nodeType] ?? null
}

/**
 * Determines a group's current media category from its children.
 * Returns null if the group is empty (accepts any supported type).
 *
 * @param childNodeIds - The group's in_child_node_ids array
 * @param getNodeType - A function that returns a node's type given its ID
 */
export function getGroupCategory(
  childNodeIds: (string | null)[],
  getNodeType: (nodeId: string) => string | undefined,
): MediaCategory | null {
  const firstActualId = childNodeIds.find((id): id is string => id !== null)
  if (!firstActualId) return null

  const firstChildType = getNodeType(firstActualId)
  if (!firstChildType) return null

  return getMediaCategory(firstChildType)
}

/**
 * Checks whether a node type can be added to a group.
 * Returns true if:
 *   - The node type is a supported media type (in MEDIA_CATEGORIES), AND
 *   - The group is empty (any supported type accepted), OR
 *   - The node's category matches the group's existing category
 *
 * @param nodeType - The type of the node to add
 * @param groupCategory - The group's current category (null if empty)
 */
export function isNodeTypeAllowed(
  nodeType: string,
  groupCategory: MediaCategory | null,
): boolean {
  const category = getMediaCategory(nodeType)
  if (category === null) return false // unsupported type
  if (groupCategory === null) return true // empty group accepts any supported type
  return category === groupCategory
}

/**
 * Returns the default node type for adding a child to a group.
 * - If the group has a category, returns a default type for that category.
 * - If the group is empty, returns 'generatedImageWithHistory' (spec req 32).
 */
export function getDefaultChildType(groupCategory: MediaCategory | null): string {
  if (groupCategory === 'video') return 'videoGenerator'
  if (groupCategory === 'image') return 'imageGenerator'
  return 'generatedImageWithHistory' // empty group defaults per spec req 32
}
